import { rngNext } from '@vpgc/core/rng';
import {
  CARRIER_DRONE_BASE_SPEED,
  CARRIER_DRONE_UPGRADED_SPEED,
  DRILL_STEP_SECONDS,
  GATHERER_BASE_SECONDS,
  HOME_TILE_X,
  HOME_TILE_Y,
  RESOURCE_IDS,
  RESOURCE_LABELS,
  RESOURCE_STACK_LIMIT,
  TRASH_STACK_LIMIT,
  TILE_SIZE,
  canAfford,
  createEmptyResources,
} from '../domain/content.js';
import type { ResourceBag, ResourceId } from '../domain/content.js';
import type { BuildCommand, BuildingId, BuildingState, BuildingType, Direction, GameState, InventoryItem, InventoryLocation, PanelCommand, ViewBuildOption } from '../contract.js';
import { addResourceItem, addTrashToSlotArray, removeResourceItem, trashCapacity } from './inventory.js';
import { addResourceToSlotArray, resourceCapacity } from './resource-stacks.js';
import { isGooTile } from './goo.js';
import { getTileAmount, isPermanentWall, setTileAmount } from './tiles.js';

const RECYCLER_SECONDS = 1.85;
const STORAGE_DEPOT_SLOTS = 8;
const GATHERER_OUTBOX_SLOTS = 8;
const CARRIER_CARGO_SLOTS = 4;
const DRILL_FUEL_PER_STEP = 1;

export const BUILD_COSTS: Record<BuildingType, ResourceBag> = {
  recycler: { ...createEmptyResources(), scrap: 3, plastic: 1 },
  storage: { ...createEmptyResources(), scrap: 5, plastic: 2 },
  gatherer: { ...createEmptyResources(), scrap: 8, plastic: 3 },
  carrier_drone: { ...createEmptyResources(), scrap: 10, plastic: 5, glass: 2 },
  drill: { ...createEmptyResources(), scrap: 12, plastic: 2, biomass: 4 },
};

export const BUILD_OPTIONS: readonly ViewBuildOption[] = [
  {
    type: 'recycler',
    name: 'Recycler',
    description: 'Turns stacked trash input into resources over time.',
    footprint: '1x1',
    cost: BUILD_COSTS.recycler,
    canBuild: false,
  },
  {
    type: 'storage',
    name: 'Storage Depot',
    description: 'Stores spare inventory items in an 8-slot 2x2 depot.',
    footprint: '2x2',
    cost: BUILD_COSTS.storage,
    canBuild: false,
  },
  {
    type: 'gatherer',
    name: 'Gatherer',
    description: 'Slowly gathers nearby trash into its outbox.',
    footprint: '1x1',
    cost: BUILD_COSTS.gatherer,
    canBuild: false,
  },
  {
    type: 'carrier_drone',
    name: 'Carrier Drone',
    description: 'Launches a drone that carries trash from player to recycler.',
    footprint: '1x1',
    cost: BUILD_COSTS.carrier_drone,
    canBuild: false,
  },
  {
    type: 'drill',
    name: 'Drill',
    description: 'Fueled by biomass, then carves a straight line through trash.',
    footprint: '1x1',
    cost: BUILD_COSTS.drill,
    canBuild: false,
  },
];

export const ACTIVE_BUILD_OPTIONS: readonly ViewBuildOption[] = BUILD_OPTIONS.filter((option) => option.type !== 'drill');

export function createInitialBuildings(): BuildingState[] {
  return [
    {
      id: 'home_recycler',
      type: 'recycler',
      name: 'Home Recycler',
      tileX: HOME_TILE_X,
      tileY: HOME_TILE_Y,
      width: 1,
      height: 1,
      inbox: emptySlots(1),
      outbox: emptySlots(6),
      storage: [],
      active: null,
      workTimerSec: 0,
    },
  ];
}

export function createBuilding(type: BuildingType, id: BuildingId, tileX: number, tileY: number, facing: Direction = 'south'): BuildingState {
  if (type === 'storage') {
    return baseBuilding(id, type, 'Storage Depot', tileX, tileY, 2, 2, [], [], emptySlots(STORAGE_DEPOT_SLOTS));
  }
  if (type === 'gatherer') {
    return baseBuilding(id, type, 'Gatherer', tileX, tileY, 1, 1, [], emptySlots(GATHERER_OUTBOX_SLOTS), []);
  }
  if (type === 'carrier_drone') {
    return baseBuilding(id, type, 'Carrier Drone Dock', tileX, tileY, 1, 1, [], [], []);
  }
  if (type === 'drill') {
    return {
      ...baseBuilding(id, type, 'Drill', tileX, tileY, 1, 1, emptySlots(1), [], []),
      facing,
      fuel: 0,
    };
  }
  return baseBuilding(id, type, 'Recycler', tileX, tileY, 1, 1, emptySlots(1), emptySlots(6), []);
}

export function cloneBuilding(building: BuildingState): BuildingState {
  return {
    ...building,
    inbox: building.inbox.map(cloneItem),
    outbox: building.outbox.map(cloneItem),
    storage: building.storage.map(cloneItem),
    active: building.active === null
      ? null
      : {
        ...building.active,
        item: { ...building.active.item },
        outputResources: [...building.active.outputResources],
      },
  };
}

export function buildingById(state: GameState, id: BuildingId | null): BuildingState | undefined {
  if (id === null) return undefined;
  return state.buildings.find((building) => building.id === id);
}

export function getBuildingAtTile(state: GameState, x: number, y: number): BuildingState | undefined {
  return state.buildings.find((building) => containsTile(building, x, y));
}

export function handleBuildCommand(state: GameState, command: BuildCommand): void {
  if (command === 'open') {
    state.ui.buildMenuOpen = true;
    state.ui.openPanelBuildingId = null;
    state.ui.pendingBuildType = null;
    state.ui.inventoryOpen = false;
    state.ui.techTreeOpen = false;
    state.ui.toolsPanelOpen = false;
    state.ui.debugPanelOpen = false;
    return;
  }
  if (command === 'close') {
    state.ui.buildMenuOpen = false;
    return;
  }
  if (command === 'cancel') {
    state.ui.buildMenuOpen = false;
    state.ui.pendingBuildType = null;
    return;
  }
  if (command === 'drill' || !canAffordBuilding(state, command)) return;
  state.ui.buildMenuOpen = false;
  state.ui.openPanelBuildingId = null;
  state.ui.pendingBuildType = command;
  state.ui.inventoryOpen = false;
  state.ui.techTreeOpen = false;
  state.ui.toolsPanelOpen = false;
  state.ui.debugPanelOpen = false;
}

export function canPlacePendingBuilding(state: GameState, tileX: number, tileY: number, playerTileX: number, playerTileY: number): boolean {
  const type = state.ui.pendingBuildType;
  if (type === null) return false;
  const footprint = footprintForType(type);
  if (!footprintTouchesPlayer(tileX, tileY, footprint.width, footprint.height, playerTileX, playerTileY)) return false;
  if (!canAffordBuilding(state, type)) return false;
  for (let y = tileY; y < tileY + footprint.height; y++) {
    for (let x = tileX; x < tileX + footprint.width; x++) {
      if (getTileAmount(state, x, y) > 0) return false;
      if (isGooTile(state, x, y)) return false;
      if (isPermanentWall(x, y)) return false;
      if (getBuildingAtTile(state, x, y) !== undefined) return false;
    }
  }
  return true;
}

export function placePendingBuilding(state: GameState, tileX: number, tileY: number): boolean {
  const type = state.ui.pendingBuildType;
  if (type === null) return false;
  if (!canAffordBuilding(state, type)) return false;
  spendBuildCost(state, BUILD_COSTS[type]);
  const id = `${type}_${state.nextBuildingId++}`;
  state.buildings.push(createBuilding(type, id, tileX, tileY, state.player.facing));
  if (type === 'carrier_drone') {
    state.mobileUnits.push({
      id: `carrier_${id}`,
      type: 'carrier_drone',
      sourceBuildingId: id,
      x: tileX * TILE_SIZE + TILE_SIZE * 0.5,
      y: tileY * TILE_SIZE + TILE_SIZE * 0.5,
      mode: 'to_port',
      cargo: emptySlots(CARRIER_CARGO_SLOTS),
      speed: state.tech.levels.carrier_drone >= 2 ? CARRIER_DRONE_UPGRADED_SPEED : CARRIER_DRONE_BASE_SPEED,
    });
  }
  state.stats.buildingsBuilt += 1;
  state.ui.pendingBuildType = null;
  return true;
}

export function canAffordBuilding(state: GameState, type: BuildingType): boolean {
  if (!isBuildingUnlocked(state, type)) return false;
  const cost = BUILD_COSTS[type];
  if (!canAfford(state.resources, cost)) return false;
  for (const id of RESOURCE_IDS) {
    if (cost[id] <= 0) continue;
    let available = 0;
    for (const slot of state.inventory.slots) {
      if (slot?.kind === 'resource' && slot.resource === id) available += slot.quantity;
    }
    if (available < cost[id]) return false;
  }
  return true;
}

export function isBuildingUnlocked(state: GameState, type: BuildingType): boolean {
  if (type === 'recycler' || type === 'storage') return true;
  if (type === 'gatherer') return state.tech.levels.gatherer > 0;
  if (type === 'carrier_drone') return state.tech.levels.carrier_drone > 0;
  if (type === 'drill') return state.tech.levels.drill > 0;
  return false;
}

export function updateBuildings(state: GameState, dtSec: number, addFx: (kind: 'dropoff' | 'pickup' | 'burn' | 'blast', resource: ResourceId, fromX: number, fromY: number, toX: number, toY: number) => void): void {
  for (const building of state.buildings) {
    if (building.type === 'recycler') updateRecycler(state, building, dtSec, addFx);
    else if (building.type === 'gatherer') updateGatherer(state, building, dtSec, addFx);
    else if (building.type === 'drill') updateDrill(state, building, dtSec, addFx);
  }
  updateCarrierDrones(state, dtSec, addFx);
}

export function handlePanelCommand(state: GameState, command: PanelCommand): void {
  const building = buildingById(state, state.ui.openPanelBuildingId);
  if (command === 'close' || (state.ui.openPanelBuildingId !== null && building === undefined)) {
    state.ui.openPanelBuildingId = null;
    return;
  }
  if (building === undefined) return;
  if (command === 'removeBuilding') {
    removeBuilding(state, building);
    return;
  }
  if (command === 'depositSelected') {
    depositSelected(state, building);
    return;
  }
  if (command === 'depositAll') {
    depositAll(state, building);
    return;
  }
  if (command === 'collectOutput') {
    collectOutput(state, building);
  }
}

export function handleTransferItem(state: GameState, from: InventoryLocation, slot: number): void {
  const building = buildingById(state, state.ui.openPanelBuildingId);
  if (building === undefined) return;
  state.ui.statusMessage = null;
  if (from === 'player') {
    const item = state.inventory.slots[slot] ?? null;
    if (item === null) return;
    if (!canAcceptFromPlayer(building, item)) {
      state.ui.statusMessage = `${building.name} cannot accept ${itemLabel(item)}.`;
      return;
    }
    if (building.type === 'recycler') {
      transferPlayerTrashToRecycler(state, building, slot);
      return;
    }
    if (building.type === 'drill') {
      clearPlayerSlot(state, slot);
      building.fuel = (building.fuel ?? 0) + item.quantity;
      return;
    }
    if (addItemToSlots(state, targetStorageSlots(building), item, { trashStacks: true })) {
      clearPlayerSlot(state, slot);
    } else {
      state.ui.statusMessage = `${building.name} is full.`;
    }
    return;
  }

  const slots = slotsForLocation(building, from);
  const item = slots[slot] ?? null;
  if (item === null) return;
  if (addItemToPlayerInventory(state, item)) {
    slots[slot] = null;
  } else {
    state.ui.statusMessage = 'Inventory is full.';
  }
}

export function handlePanelItemMove(state: GameState, from: InventoryLocation, fromSlot: number, to: InventoryLocation, toSlot: number): void {
  const building = buildingById(state, state.ui.openPanelBuildingId);
  if (building === undefined) return;
  state.ui.statusMessage = null;

  if (from === to) {
    moveWithinLocation(state, building, from, fromSlot, toSlot);
    return;
  }

  if (from === 'player') {
    movePlayerItemToBuilding(state, building, fromSlot, to, toSlot);
    return;
  }

  if (to === 'player') {
    moveBuildingItemToPlayer(state, building, from, fromSlot, toSlot);
    return;
  }

  moveWithinBuilding(state, building, from, fromSlot, to, toSlot);
}

export function canDepositSelected(state: GameState, building: BuildingState): boolean {
  const selected = state.inventory.slots[state.player.selectedSlot] ?? null;
  return selected !== null && canAcceptFromPlayer(building, selected);
}

export function canCollectOutput(building: BuildingState): boolean {
  return building.outbox.some((slot) => slot !== null) || building.storage.some((slot) => slot !== null);
}

export function canRemoveBuilding(building: BuildingState): boolean {
  return building.id !== 'home_recycler' && building.active === null && slotsEmpty(building.inbox) && slotsEmpty(building.outbox) && slotsEmpty(building.storage) && (building.fuel ?? 0) <= 0;
}

export function buildingAcceptsText(building: BuildingState): string {
  if (building.type === 'recycler') return 'Trash';
  if (building.type === 'drill') return 'Biomass fuel';
  if (building.type === 'gatherer') return 'Outbox only';
  if (building.type === 'carrier_drone') return 'Drone dock';
  return 'Any inventory item';
}

export function buildingStatus(building: BuildingState): string {
  if (building.type === 'storage') return `${building.storage.filter((slot) => slot !== null).length}/${building.storage.length} slots used`;
  if (building.type === 'gatherer') return `Gathering radius ${building.outbox.length > 0 ? 'active' : 'blocked'}`;
  if (building.type === 'carrier_drone') return 'Drone running';
  if (building.type === 'drill') return `Fuel ${building.fuel ?? 0}`;
  if (building.active !== null && building.active.elapsedSec >= building.active.durationSec) {
    const blockedResource = firstBlockedOutputResource(building, building.active.outputResources);
    if (blockedResource !== null) return `Output full: ${RESOURCE_LABELS[blockedResource]}`;
  }
  if (building.active !== null) return 'Processing';
  if (hasProcessInput(building)) return 'Ready';
  return 'Waiting for input';
}

export function buildingProgress(building: BuildingState): number {
  if (building.type === 'gatherer') return Math.min(1, building.workTimerSec / GATHERER_BASE_SECONDS);
  if (building.type === 'drill') return (building.fuel ?? 0) > 0 ? Math.min(1, building.workTimerSec / DRILL_STEP_SECONDS) : 0;
  if (building.active === null) return 0;
  return Math.min(1, building.active.elapsedSec / building.active.durationSec);
}

function updateRecycler(state: GameState, building: BuildingState, dtSec: number, addFx: (kind: 'dropoff', resource: ResourceId, fromX: number, fromY: number, toX: number, toY: number) => void): void {
  if (building.active === null) startNextProcess(state, building);
  if (building.active === null) return;

  building.active.elapsedSec = Math.min(building.active.durationSec, building.active.elapsedSec + dtSec);
  if (building.active.elapsedSec < building.active.durationSec) return;
  if (!hasOutputSpaceForAll(building, building.active.outputResources)) return;

  for (const resource of building.active.outputResources) {
    addResourceToSlots(state, building.outbox, resource, 1);
    const x = building.tileX * TILE_SIZE + TILE_SIZE * 0.5;
    const y = building.tileY * TILE_SIZE + TILE_SIZE * 0.5;
    addFx('dropoff', resource, x, y, x, y - 12);
  }
  state.stats.garbageRecycled += 1;
  building.active = null;
}

function updateGatherer(state: GameState, building: BuildingState, dtSec: number, addFx: (kind: 'pickup', resource: ResourceId, fromX: number, fromY: number, toX: number, toY: number) => void): void {
  if (trashCapacity(building.outbox) <= 0) return;
  building.workTimerSec += dtSec;
  if (building.workTimerSec < GATHERER_BASE_SECONDS) return;
  building.workTimerSec = 0;
  const target = findGathererTarget(state, building);
  if (target === null) return;
  const amount = getTileAmount(state, target.x, target.y);
  const resource = trashKindForTile(state, target.x, target.y, amount);
  const item = createTrashStack(state, resource, 1);
  if (!addItemToSlots(state, building.outbox, item, { trashStacks: true })) return;
  setTileAmount(state, target.x, target.y, amount - 1);
  state.stats.garbageHarvested += 1;
  if (amount - 1 <= 0) state.stats.tilesCleared += 1;
  addFx('pickup', resource, target.x * TILE_SIZE + TILE_SIZE * 0.5, target.y * TILE_SIZE + TILE_SIZE * 0.5, building.tileX * TILE_SIZE + TILE_SIZE * 0.5, building.tileY * TILE_SIZE + TILE_SIZE * 0.5);
}

function updateDrill(state: GameState, building: BuildingState, dtSec: number, addFx: (kind: 'burn', resource: ResourceId, fromX: number, fromY: number, toX: number, toY: number) => void): void {
  if ((building.fuel ?? 0) <= 0) return;
  building.workTimerSec += dtSec;
  if (building.workTimerSec < DRILL_STEP_SECONDS) return;
  building.workTimerSec = 0;
  building.fuel = Math.max(0, (building.fuel ?? 0) - DRILL_FUEL_PER_STEP);
  const delta = directionVector(building.facing ?? 'south');
  building.tileX += delta.x;
  building.tileY += delta.y;
  const amount = getTileAmount(state, building.tileX, building.tileY);
  if (amount <= 0) return;
  const resource = trashKindForTile(state, building.tileX, building.tileY, amount);
  setTileAmount(state, building.tileX, building.tileY, 0);
  state.stats.tilesCleared += 1;
  state.stats.trashDestroyed += amount;
  addFx('burn', resource, building.tileX * TILE_SIZE + TILE_SIZE * 0.5, building.tileY * TILE_SIZE + TILE_SIZE * 0.5, building.tileX * TILE_SIZE + TILE_SIZE * 0.5, building.tileY * TILE_SIZE + TILE_SIZE * 0.5);
}

function updateCarrierDrones(state: GameState, dtSec: number, addFx: (kind: 'pickup' | 'dropoff', resource: ResourceId, fromX: number, fromY: number, toX: number, toY: number) => void): void {
  for (const unit of state.mobileUnits) {
    const port = buildingById(state, unit.sourceBuildingId);
    if (port === undefined) continue;
    const recycler = nearestRecycler(state, unit.x, unit.y);
    const hasCargo = unit.cargo.some((slot) => slot !== null);
    const playerHasTrash = hasPlayerTrash(state);
    if (hasCargo && recycler !== null) {
      unit.mode = 'to_recycler';
    } else if (!hasCargo && playerHasTrash && recycler !== null) {
      unit.mode = 'to_player';
    } else {
      unit.mode = 'to_port';
    }

    const portPosition = buildingCenter(port);
    const recyclerPosition = recycler === null ? portPosition : buildingCenter(recycler);
    const target = unit.mode === 'to_player' ? { x: state.player.x, y: state.player.y } : unit.mode === 'to_recycler' ? recyclerPosition : portPosition;
    moveUnitToward(unit, target.x, target.y, dtSec);
    if (Math.hypot(unit.x - target.x, unit.y - target.y) > TILE_SIZE * 0.45) continue;
    if (unit.mode === 'to_player') {
      takeTrashFromPlayer(state, unit);
      unit.mode = unit.cargo.some((slot) => slot !== null) && nearestRecycler(state, unit.x, unit.y) !== null ? 'to_recycler' : 'to_port';
    } else {
      if (unit.mode === 'to_recycler' && recycler !== null) unloadDroneToRecycler(state, unit, recycler, addFx);
      unit.mode = hasPlayerTrash(state) && nearestRecycler(state, unit.x, unit.y) !== null ? 'to_player' : 'to_port';
    }
  }
}

function depositSelected(state: GameState, building: BuildingState): boolean {
  return depositSlot(state, building, state.player.selectedSlot);
}

function depositAll(state: GameState, building: BuildingState): void {
  for (let i = 0; i < state.inventory.slots.length; i++) {
    const item = state.inventory.slots[i];
    if (item === null || !canAcceptFromPlayer(building, item)) continue;
    if (!depositSlot(state, building, i)) return;
  }
}

function depositSlot(state: GameState, building: BuildingState, slotIndex: number): boolean {
  const selected = state.inventory.slots[slotIndex] ?? null;
  if (selected === null || !canAcceptFromPlayer(building, selected)) return false;
  if (building.type === 'recycler') {
    return transferPlayerTrashToRecycler(state, building, slotIndex);
  }
  if (building.type === 'drill') {
    clearPlayerSlot(state, slotIndex);
    building.fuel = (building.fuel ?? 0) + selected.quantity;
    return true;
  }
  if (!addItemToSlots(state, targetStorageSlots(building), selected, { trashStacks: true })) return false;
  clearPlayerSlot(state, slotIndex);
  return true;
}

function collectOutput(state: GameState, building: BuildingState): void {
  const slots = building.type === 'storage' ? building.storage : building.outbox;
  for (let i = 0; i < slots.length; i++) {
    const item = slots[i];
    if (item === null) continue;
    if (!addItemToPlayerInventory(state, item)) continue;
    slots[i] = null;
  }
}

function moveWithinLocation(state: GameState, building: BuildingState, location: InventoryLocation, fromSlot: number, toSlot: number): void {
  if (fromSlot === toSlot) return;
  const slots = location === 'player' ? state.inventory.slots : slotsForLocation(building, location);
  if (location !== 'player' && location !== 'buildingStorage') return;
  const moving = slots[fromSlot] ?? null;
  const target = slots[toSlot] ?? null;
  slots[toSlot] = moving;
  slots[fromSlot] = target;
  if (location === 'player') {
    state.player.selectedSlot = Math.max(0, Math.min(state.inventory.slots.length - 1, toSlot));
    state.ui.inventoryFocus = state.player.selectedSlot;
  }
}

function movePlayerItemToBuilding(state: GameState, building: BuildingState, fromSlot: number, to: InventoryLocation, toSlot: number): void {
  const item = state.inventory.slots[fromSlot] ?? null;
  if (item === null) return;
  if (!canAcceptFromPlayer(building, item)) {
    state.ui.statusMessage = `${building.name} cannot accept ${itemLabel(item)}.`;
    return;
  }
  if (building.type === 'recycler') {
    if (to !== 'buildingInput') return;
    transferPlayerTrashToRecycler(state, building, fromSlot);
    return;
  }
  if (building.type === 'drill') {
    if (to !== 'buildingInput') return;
    clearPlayerSlot(state, fromSlot);
    building.fuel = (building.fuel ?? 0) + item.quantity;
    return;
  }
  if (building.type !== 'storage' || to !== 'buildingStorage') return;
  if (placeItemIntoSlotArray(state, building.storage, item, toSlot, { trashStacks: true })) clearPlayerSlot(state, fromSlot);
  else state.ui.statusMessage = `${building.name} cannot place that there.`;
}

function moveBuildingItemToPlayer(state: GameState, building: BuildingState, from: InventoryLocation, fromSlot: number, toSlot: number): void {
  const slots = slotsForLocation(building, from);
  const item = slots[fromSlot] ?? null;
  if (item === null) return;
  if (placeItemIntoPlayerSlot(state, item, toSlot)) slots[fromSlot] = null;
  else if (addItemToPlayerInventory(state, item)) slots[fromSlot] = null;
  else state.ui.statusMessage = 'Inventory is full.';
}

function moveWithinBuilding(state: GameState, building: BuildingState, from: InventoryLocation, fromSlot: number, to: InventoryLocation, toSlot: number): void {
  if (building.type !== 'storage' || from !== 'buildingStorage' || to !== 'buildingStorage') return;
  moveWithinLocation(state, building, 'buildingStorage', fromSlot, toSlot);
}

function placeItemIntoPlayerSlot(state: GameState, item: InventoryItem, slot: number): boolean {
  if (slot < 0 || slot >= state.inventory.slots.length) return false;
  if (item.kind === 'item') {
    if (item.quantity !== 1 || state.inventory.slots[slot] !== null) return false;
    state.inventory.slots[slot] = { ...item, uid: state.inventory.nextItemUid++ };
    return true;
  }
  const target = state.inventory.slots[slot] ?? null;
  if (item.kind === 'trash') {
    if (target !== null && (target.kind !== 'trash' || target.quantity + item.quantity > TRASH_STACK_LIMIT)) return false;
    if (target === null) state.inventory.slots[slot] = { ...item, uid: state.inventory.nextItemUid++ };
    else target.quantity += item.quantity;
    return true;
  }
  if (target !== null && (target.kind !== 'resource' || target.resource !== item.resource || target.quantity + item.quantity > RESOURCE_STACK_LIMIT)) return false;
  if (target === null) {
    state.inventory.slots[slot] = { ...item, uid: state.inventory.nextItemUid++ };
    state.resources[item.resource ?? 'scrap'] += item.quantity;
    return true;
  }
  target.quantity += item.quantity;
  state.resources[item.resource ?? 'scrap'] += item.quantity;
  return true;
}

function placeItemIntoSlotArray(
  state: GameState,
  slots: Array<InventoryItem | null>,
  item: InventoryItem,
  slot: number,
  options: { trashStacks: boolean },
): boolean {
  if (slot < 0 || slot >= slots.length) return false;
  const target = slots[slot] ?? null;
  if (item.kind === 'resource') {
    if (target !== null && (target.kind !== 'resource' || target.resource !== item.resource || target.quantity + item.quantity > RESOURCE_STACK_LIMIT)) return false;
    if (target === null) slots[slot] = { ...item };
    else target.quantity += item.quantity;
    return true;
  }
  if (item.kind === 'trash' && options.trashStacks && target?.kind === 'trash') {
    if (target.quantity + item.quantity > TRASH_STACK_LIMIT) return false;
    target.quantity += item.quantity;
    return true;
  }
  if (target !== null) return false;
  slots[slot] = { ...item };
  return true;
}

function removeBuilding(state: GameState, building: BuildingState): void {
  if (!canRemoveBuilding(building)) {
    state.ui.statusMessage = building.id === 'home_recycler' ? 'The starter recycler cannot be removed.' : 'Empty this building before removing it.';
    return;
  }
  const index = state.buildings.findIndex((candidate) => candidate.id === building.id);
  if (index < 0) return;
  state.buildings.splice(index, 1);
  state.mobileUnits = state.mobileUnits.filter((unit) => unit.sourceBuildingId !== building.id);
  state.ui.openPanelBuildingId = null;
  state.ui.statusMessage = `${building.name} removed.`;
}

function startNextProcess(state: GameState, building: BuildingState): void {
  const item = takeProcessInput(building);
  if (item === null) return;
  building.active = {
    item,
    elapsedSec: 0,
    durationSec: RECYCLER_SECONDS,
    outputResources: rollRecyclerOutputs(state),
  };
}

function rollRecyclerOutputs(state: GameState): ResourceId[] {
  const zeroChance = state.tech.levels.recycler_efficiency > 0 ? 0.175 : 0.35;
  const oneChance = 0.35;
  const twoChance = 0.2;
  const threeChance = 0.1;
  const scale = (1 - zeroChance) / (oneChance + twoChance + threeChance);
  const thresholds = [
    zeroChance,
    zeroChance + oneChance * scale,
    zeroChance + (oneChance + twoChance) * scale,
  ];
  const roll = nextUnitFloat(state);
  const count = roll < thresholds[0] ? 0 : roll < thresholds[1] ? 1 : roll < thresholds[2] ? 2 : 3;
  const outputs: ResourceId[] = [];
  for (let i = 0; i < count; i++) outputs.push(RESOURCE_IDS[Math.floor(nextUnitFloat(state) * RESOURCE_IDS.length) % RESOURCE_IDS.length]);
  return outputs;
}

function takeProcessInput(building: BuildingState): InventoryItem | null {
  const item = building.inbox[0] ?? null;
  if (item === null || item.kind !== 'trash' || item.quantity <= 0) return null;
  item.quantity -= 1;
  const unit: InventoryItem = {
    uid: item.uid,
    kind: 'trash',
    itemId: 'trash',
    resource: item.resource ?? 'scrap',
    quantity: 1,
  };
  if (item.quantity <= 0) building.inbox[0] = null;
  return unit;
}

function canAcceptFromPlayer(building: BuildingState, item: InventoryItem): boolean {
  if (building.type === 'recycler') return item.kind === 'trash' && recyclerTrashCapacity(building) > 0;
  if (building.type === 'drill') return item.kind === 'resource' && item.resource === 'biomass';
  if (building.type === 'gatherer' || building.type === 'carrier_drone') return false;
  if (item.kind === 'resource') return hasStackSpace(building.storage, item.resource ?? 'scrap') || hasEmptySlot(building.storage);
  if (item.kind === 'trash') return trashCapacity(building.storage) > 0;
  return hasEmptySlot(building.storage);
}

function addTrashToRecyclerInput(state: GameState, building: BuildingState, item: InventoryItem): number {
  const accepted = Math.min(item.quantity, recyclerTrashCapacity(building));
  if (accepted <= 0) return 0;
  const existing = building.inbox[0];
  if (existing !== null) {
    existing.quantity += accepted;
    return accepted;
  }
  building.inbox[0] = {
    ...item,
    uid: state.inventory.nextItemUid++,
    quantity: accepted,
  };
  return accepted;
}

function addItemToPlayerInventory(state: GameState, item: InventoryItem): boolean {
  if (item.kind === 'resource') return addResourceToPlayerInventory(state, item.resource ?? 'scrap', item.quantity);
  if (item.kind === 'item') return addSingleItemsToPlayerInventory(state, item);
  return addTrashToSlotArray(state, state.inventory.slots, item.resource ?? 'scrap', item.quantity);
}

function addSingleItemsToPlayerInventory(state: GameState, item: InventoryItem): boolean {
  const emptySlots = state.inventory.slots.filter((slot) => slot === null).length;
  if (emptySlots < item.quantity) return false;
  for (let i = 0; i < item.quantity; i++) {
    const slot = state.inventory.slots.findIndex((candidate) => candidate === null);
    if (slot < 0) return false;
    state.inventory.slots[slot] = { ...item, uid: state.inventory.nextItemUid++, quantity: 1 };
  }
  return true;
}

function addResourceToPlayerInventory(state: GameState, resource: ResourceId, amount: number): boolean {
  return addResourceItem(state, resource, amount);
}

function addItemToSlots(
  state: GameState,
  slots: Array<InventoryItem | null>,
  item: InventoryItem,
  options: { trashStacks: boolean },
): boolean {
  if (item.kind === 'resource') return addResourceToSlots(state, slots, item.resource ?? 'scrap', item.quantity);
  if (item.kind === 'trash' && options.trashStacks) {
    return addTrashToSlotArray(state, slots, item.resource ?? 'scrap', item.quantity);
  }
  const slot = slots.findIndex((candidate) => candidate === null);
  if (slot < 0) return false;
  slots[slot] = { ...item };
  return true;
}

function addResourceToSlots(state: GameState, slots: Array<InventoryItem | null>, resource: ResourceId, amount: number): boolean {
  return addResourceToSlotArray(state, slots, resource, amount);
}

function clearPlayerSlot(state: GameState, slot: number): void {
  const item = state.inventory.slots[slot];
  if (item?.kind === 'resource' && item.resource !== undefined) state.resources[item.resource] -= item.quantity;
  state.inventory.slots[slot] = null;
}

function spendBuildCost(state: GameState, cost: ResourceBag): void {
  for (const id of RESOURCE_IDS) {
    if (cost[id] <= 0) continue;
    removeResourceItem(state, id, cost[id]);
  }
}

function findGathererTarget(state: GameState, building: BuildingState): { x: number; y: number } | null {
  const radius = state.tech.levels.gatherer >= 2 ? 3 : 2;
  let best: { x: number; y: number; amount: number; dist: number } | null = null;
  for (let y = building.tileY - radius; y <= building.tileY + radius; y++) {
    for (let x = building.tileX - radius; x <= building.tileX + radius; x++) {
      const dist = Math.abs(x - building.tileX) + Math.abs(y - building.tileY);
      if (dist > radius || dist === 0) continue;
      const amount = getTileAmount(state, x, y);
      if (amount <= 0) continue;
      if (best === null || amount > best.amount || (amount === best.amount && dist < best.dist)) best = { x, y, amount, dist };
    }
  }
  return best;
}

function takeTrashFromPlayer(state: GameState, unit: GameState['mobileUnits'][number]): void {
  for (let i = 0; i < state.inventory.slots.length; i++) {
    const cargoSlot = unit.cargo.findIndex((slot) => slot === null);
    if (cargoSlot < 0) return;
    const item = state.inventory.slots[i];
    if (item?.kind !== 'trash') continue;
    unit.cargo[cargoSlot] = item;
    state.inventory.slots[i] = null;
  }
}

function unloadDroneToRecycler(state: GameState, unit: GameState['mobileUnits'][number], recycler: BuildingState, addFx: (kind: 'dropoff', resource: ResourceId, fromX: number, fromY: number, toX: number, toY: number) => void): void {
  for (let i = 0; i < unit.cargo.length; i++) {
    const item = unit.cargo[i];
    if (item === null) continue;
    const accepted = addTrashToRecyclerInput(state, recycler, item);
    if (accepted <= 0) return;
    item.quantity -= accepted;
    if (item.quantity <= 0) unit.cargo[i] = null;
    addFx('dropoff', item.resource ?? 'scrap', unit.x, unit.y, recycler.tileX * TILE_SIZE + TILE_SIZE * 0.5, recycler.tileY * TILE_SIZE + TILE_SIZE * 0.5);
  }
}

function transferPlayerTrashToRecycler(state: GameState, building: BuildingState, slotIndex: number): boolean {
  const item = state.inventory.slots[slotIndex];
  if (item?.kind !== 'trash') return false;
  const requested = item.quantity;
  const accepted = addTrashToRecyclerInput(state, building, item);
  if (accepted <= 0) return false;
  item.quantity -= accepted;
  if (item.quantity <= 0) state.inventory.slots[slotIndex] = null;
  if (accepted < requested) state.ui.statusMessage = `${building.name} input is full.`;
  return true;
}

function recyclerTrashCapacity(building: BuildingState): number {
  const existing = building.inbox[0];
  if (existing === null) return TRASH_STACK_LIMIT;
  return existing.kind === 'trash' ? Math.max(0, TRASH_STACK_LIMIT - existing.quantity) : 0;
}

function nearestRecycler(state: GameState, x: number, y: number): BuildingState | null {
  let best: BuildingState | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const building of state.buildings) {
    if (building.type !== 'recycler') continue;
    const bx = building.tileX * TILE_SIZE + TILE_SIZE * 0.5;
    const by = building.tileY * TILE_SIZE + TILE_SIZE * 0.5;
    const dist = Math.hypot(bx - x, by - y);
    if (dist < bestDist) {
      best = building;
      bestDist = dist;
    }
  }
  return best;
}

function moveUnitToward(unit: GameState['mobileUnits'][number], targetX: number, targetY: number, dtSec: number): void {
  const dx = targetX - unit.x;
  const dy = targetY - unit.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= 0.001) return;
  const move = Math.min(dist, unit.speed * dtSec);
  unit.x += (dx / dist) * move;
  unit.y += (dy / dist) * move;
}

function hasOutputSpaceForAll(building: BuildingState, resources: ResourceId[]): boolean {
  return firstBlockedOutputResource(building, resources) === null;
}

function firstBlockedOutputResource(building: BuildingState, resources: ResourceId[]): ResourceId | null {
  const virtual = building.outbox.map(cloneItem);
  for (const resource of resources) {
    if (!addResourceToVirtualSlots(virtual, resource)) return resource;
  }
  return null;
}

function addResourceToVirtualSlots(slots: Array<InventoryItem | null>, resource: ResourceId): boolean {
  const existing = slots.find((slot) => slot?.kind === 'resource' && slot.resource === resource);
  if (existing !== undefined && existing !== null && existing.quantity < RESOURCE_STACK_LIMIT) {
    existing.quantity += 1;
    return true;
  }
  const slot = slots.findIndex((candidate) => candidate === null);
  if (slot < 0) return false;
  slots[slot] = { uid: 0, kind: 'resource', itemId: resource, resource, quantity: 1 };
  return true;
}

function hasProcessInput(building: BuildingState): boolean {
  return building.inbox.some((item) => item?.kind === 'trash' && item.quantity > 0);
}

function hasStackSpace(slots: Array<InventoryItem | null>, resource: ResourceId): boolean {
  return resourceCapacity(slots, resource) > 0;
}

function hasEmptySlot(slots: Array<InventoryItem | null>): boolean {
  return slots.some((slot) => slot === null);
}

function slotsEmpty(slots: Array<InventoryItem | null>): boolean {
  return slots.every((slot) => slot === null);
}

function slotsForLocation(building: BuildingState, location: InventoryLocation): Array<InventoryItem | null> {
  if (location === 'buildingInput') return building.inbox;
  if (location === 'buildingOutput') return building.outbox;
  return building.storage;
}

function targetStorageSlots(building: BuildingState): Array<InventoryItem | null> {
  return building.type === 'storage' ? building.storage : building.outbox;
}

function footprintForType(type: BuildingType): { width: number; height: number } {
  return type === 'storage' ? { width: 2, height: 2 } : { width: 1, height: 1 };
}

function footprintTouchesPlayer(tileX: number, tileY: number, width: number, height: number, playerTileX: number, playerTileY: number): boolean {
  for (let y = tileY; y < tileY + height; y++) {
    for (let x = tileX; x < tileX + width; x++) {
      if (Math.abs(x - playerTileX) + Math.abs(y - playerTileY) === 1) return true;
    }
  }
  return false;
}

function containsTile(building: BuildingState, x: number, y: number): boolean {
  return x >= building.tileX && x < building.tileX + building.width && y >= building.tileY && y < building.tileY + building.height;
}

function baseBuilding(
  id: BuildingId,
  type: BuildingType,
  name: string,
  tileX: number,
  tileY: number,
  width: number,
  height: number,
  inbox: Array<InventoryItem | null>,
  outbox: Array<InventoryItem | null>,
  storage: Array<InventoryItem | null>,
): BuildingState {
  return { id, type, name, tileX, tileY, width, height, inbox, outbox, storage, active: null, workTimerSec: 0 };
}

function emptySlots(count: number): Array<InventoryItem | null> {
  return Array.from({ length: count }, () => null);
}

function cloneItem(item: InventoryItem | null): InventoryItem | null {
  return item === null ? null : { ...item };
}

function createTrashStack(state: GameState, resource: ResourceId, quantity: number): InventoryItem {
  return { uid: state.inventory.nextItemUid++, kind: 'trash', itemId: 'trash', resource, quantity };
}

function itemLabel(item: InventoryItem): string {
  if (item.kind === 'trash') return 'trash';
  if (item.kind === 'item') return item.itemId;
  return item.resource ?? item.kind;
}

function hasPlayerTrash(state: GameState): boolean {
  return state.inventory.slots.some((slot) => slot?.kind === 'trash');
}

function buildingCenter(building: BuildingState): { x: number; y: number } {
  return {
    x: (building.tileX + building.width * 0.5) * TILE_SIZE,
    y: (building.tileY + building.height * 0.5) * TILE_SIZE,
  };
}

function nextUnitFloat(state: GameState): number {
  state.rng = rngNext(state.rng);
  return (state.rng >>> 0) / 0x100000000;
}

function trashKindForTile(state: GameState, x: number, y: number, amount: number): ResourceId {
  const mix = (state.seed ^ Math.imul(x + 101, 73856093) ^ Math.imul(y - 37, 19349663) ^ Math.imul(amount + 11, 83492791)) >>> 0;
  return RESOURCE_IDS[mix % RESOURCE_IDS.length];
}

function directionVector(direction: Direction): { x: -1 | 0 | 1; y: -1 | 0 | 1 } {
  switch (direction) {
    case 'north':
      return { x: 0, y: -1 };
    case 'east':
      return { x: 1, y: 0 };
    case 'south':
      return { x: 0, y: 1 };
    case 'west':
      return { x: -1, y: 0 };
  }
}
